import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import {
  Shell,
  SectionTitle,
  EmptyState,
  formatCurrency,
  formatDate,
  daysAgo,
  getInitials,
} from "./Common";
import { FONT_HEADING } from "../../../theme";

const TABS = [
  { key: "segments", label: "Segments" },
  { key: "orders", label: "Orders" },
  { key: "carts", label: "Abandoned Carts" },
];

// ── Small stat tile (icon + label + value + sub) ──────────────────────────

function StatTile({ icon, label, value, sub, tone = "blue" }) {
  const tones = {
    green: { bg: "#dcfce7", icon: "#15803d", value: "#15803d" },
    blue: { bg: "#dbeafe", icon: "#1d4ed8", value: "#1d4ed8" },
    purple: { bg: "#ede9fe", icon: "#6d28d9", value: "#6d28d9" },
    amber: { bg: "#fef3c7", icon: "#b45309", value: "#b45309" },
  };
  const t = tones[tone] || tones.blue;
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "10px",
        p: "12px 14px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: t.bg,
            color: t.icon,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 12,
            fontWeight: 700,
            color: "#71717a",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontFamily: FONT_HEADING,
          fontSize: 20,
          fontWeight: 700,
          color: t.value,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          mt: "2px",
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#71717a", lineHeight: 1.35 }}>
        {sub}
      </Typography>
    </Box>
  );
}

// ── Customer header card (avatar + identity + segment pill) ───────────────

function CustomerCard({ profile, recordData, segments, storeDomain }) {
  const fullName =
    profile?.name ||
    (recordData
      ? `${recordData.First_Name || ""} ${recordData.Last_Name || ""}`.trim()
      : "Unknown");
  const email = profile?.email || recordData?.Email || "";
  const createdAt = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })
    : "";

  // Pick the primary segment to surface as a green pill (per screenshot).
  const primarySegment = segments?.[0] || "New customer";

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "12px",
        p: "14px 16px",
        display: "flex",
        gap: 2,
        alignItems: "flex-start",
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          bgcolor: "#dbeafe",
          color: "#1d4ed8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT_HEADING,
          fontWeight: 700,
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        {getInitials(fullName)}
      </Box>

      {/* Identity */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: "#09090b",
            mb: "6px",
          }}
        >
          {fullName}
        </Typography>

        {email && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", color: "#52525b", mb: "4px" }}>
            <MailOutlineIcon sx={{ fontSize: 16, color: "#71717a" }} />
            <Typography sx={{ fontSize: 14, color: "#3f3f46" }}>{email}</Typography>
          </Box>
        )}

        {createdAt && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", color: "#52525b", mb: "10px" }}>
            <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: "#71717a" }} />
            <Typography sx={{ fontSize: 14, color: "#3f3f46" }}>
              Shopify customer since {createdAt}
            </Typography>
          </Box>
        )}

        {/* Segment pill button */}
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontSize: 13,
            fontWeight: 700,
            color: "#15803d",
            bgcolor: "#fff",
            border: "1.5px solid #86efac",
            px: "10px",
            py: "5px",
            borderRadius: "9999px",
          }}
        >
          <PersonOutlineOutlinedIcon sx={{ fontSize: 15 }} />
          {primarySegment}
        </Box>
      </Box>

      {/* Optional shop domain pill (top-right) */}
      {storeDomain && (
        <Box
          sx={{
            fontSize: 12,
            color: "#1e3a8a",
            bgcolor: "#dbeafe",
            border: "1px solid #bfdbfe",
            px: "8px",
            py: "2px",
            borderRadius: "9999px",
            fontFamily: "monospace",
            lineHeight: 1.4,
            flexShrink: 0,
          }}
        >
          {storeDomain}
        </Box>
      )}
    </Box>
  );
}

// ── Segments tab body ─────────────────────────────────────────────────────

function SegmentsView({ customer, recordData, storeDomain }) {
  const profile = customer?.profile || null;
  const metrics = customer?.metrics || null;

  const totalSpent = formatCurrency(metrics?.lifetime_spend || 0);
  const ordersCount = metrics?.total_orders || 0;
  const avgOrder = formatCurrency(metrics?.avg_order || 0);
  const lastOrderDate = metrics?.last_order_date;

  // Segment derivation kept from the previous version.
  const segments = [];
  if (ordersCount >= 3) {
    segments.push(`Bought ${ordersCount}× in last 90 days`);
  }
  if (parseFloat(metrics?.lifetime_spend || 0) >= 1000) {
    segments.push("Spent $1000+ last year");
  }
  if (segments.length === 0) segments.push("New customer");

  const tags = Array.isArray(profile?.tags)
    ? profile.tags.filter(Boolean)
    : typeof profile?.tags === "string"
    ? profile.tags.split(",").map((t) => t.trim()).filter(Boolean)
    : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <CustomerCard
        profile={profile}
        recordData={recordData}
        segments={segments}
        storeDomain={storeDomain}
      />

      {/* 4-up stat row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(4, 1fr)",
          },
          gap: 1.5,
        }}
      >
        <StatTile
          icon={<AttachMoneyOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Lifetime Spent"
          value={totalSpent}
          sub="Total spent on orders"
          tone="green"
        />
        <StatTile
          icon={<ShoppingBagOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Total Orders"
          value={ordersCount}
          sub="All time orders placed"
          tone="blue"
        />
        <StatTile
          icon={<BarChartOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Avg Order Value"
          value={avgOrder}
          sub="Average order value"
          tone="purple"
        />
        <StatTile
          icon={<EventOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Last Order"
          value={lastOrderDate ? daysAgo(lastOrderDate) : "N/A"}
          sub="Date of most recent order"
          tone="amber"
        />
      </Box>

      {/* Extra segment + tag context, kept compact below the headline grid */}
      {(segments.length > 1 || tags.length > 0) && (
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e4e4e7",
            borderRadius: "12px",
            p: "12px 14px",
          }}
        >
          {segments.length > 1 && (
            <>
              <SectionTitle sx={{ mb: "6px" }}>Customer Segments</SectionTitle>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: tags.length > 0 ? "10px" : 0 }}>
                {segments.map((seg, i) => (
                  <Box
                    key={i}
                    sx={{
                      fontSize: 12,
                      bgcolor: "#f4f4f5",
                      border: "1px solid #e4e4e7",
                      px: "10px",
                      py: "4px",
                      borderRadius: "9999px",
                      color: "#3f3f46",
                      fontWeight: 500,
                    }}
                  >
                    {seg}
                  </Box>
                ))}
              </Box>
            </>
          )}

          {tags.length > 0 && (
            <>
              <SectionTitle sx={{ mb: "6px" }}>Shopify Tags</SectionTitle>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                {tags.map((tag, i) => (
                  <Box
                    key={i}
                    sx={{
                      fontSize: 12,
                      bgcolor: "#f4f4f5",
                      color: "#09090b",
                      border: "1px solid #e4e4e7",
                      px: "8px",
                      py: "3px",
                      borderRadius: "4px",
                      fontWeight: 500,
                    }}
                  >
                    {tag}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Inline Shopify mark (small variant for order rows) ───────────────────

function ShopifyMarkSmall({ size = 28 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        bgcolor: "#95BF47",
        borderRadius: "7px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_HEADING,
          fontSize: size * 0.55,
          fontWeight: 800,
          color: "#fff",
          lineHeight: 1,
          fontStyle: "italic",
        }}
      >
        S
      </Typography>
    </Box>
  );
}

// ── Rich order row ────────────────────────────────────────────────────────

function OrderRow({ order, isLast }) {
  const orderName = order?.name || `#${order?.order_number || order?.id || ""}`;
  const status =
    order?.fulfillment_status || order?.financial_status || "pending";
  const isPaid = String(status).toLowerCase() === "paid" ||
    String(order?.financial_status || "").toLowerCase() === "paid";
  const isFulfilled = String(status).toLowerCase() === "fulfilled";
  const total = formatCurrency(order?.total_price);
  const date = formatDate(order?.created_at);
  const itemCount = order?.line_items_count ?? order?.line_items?.length ?? 0;
  const discount = order?.discount_code || order?.discount_codes?.[0]?.code;
  const adminUrl = order?.admin_url;

  // Status pill palette
  const pill =
    isFulfilled || isPaid
      ? { bg: "#dcfce7", fg: "#166534" }
      : String(status).toLowerCase() === "refunded" ||
        String(status).toLowerCase() === "cancelled"
      ? { bg: "#f4f4f5", fg: "#52525b" }
      : { bg: "#fef3c7", fg: "#92400e" };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: "10px",
        borderBottom: isLast ? "none" : "1px solid #f4f4f5",
      }}
    >
      <ShopifyMarkSmall size={32} />

      {/* Identity + meta */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 15,
              fontWeight: 700,
              color: "#09090b",
              letterSpacing: "-0.01em",
            }}
          >
            {orderName}
          </Typography>
          <Box
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              px: "7px",
              py: "1px",
              borderRadius: "9999px",
              bgcolor: pill.bg,
              color: pill.fg,
              textTransform: "capitalize",
            }}
          >
            {status}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: "2px" }}>
          <Typography sx={{ fontSize: 13, color: "#71717a" }}>{date}</Typography>
          <Box
            component="span"
            sx={{
              width: 3,
              height: 3,
              borderRadius: "50%",
              bgcolor: "#d4d4d8",
            }}
          />
          <Typography sx={{ fontSize: 13, color: "#71717a" }}>
            {itemCount} item{itemCount !== 1 ? "s" : ""}
          </Typography>
          {discount && (
            <>
              <Box
                component="span"
                sx={{
                  width: 3,
                  height: 3,
                  borderRadius: "50%",
                  bgcolor: "#d4d4d8",
                }}
              />
              <Typography
                sx={{
                  fontSize: 13,
                  color: "#15803d",
                  fontWeight: 600,
                }}
              >
                {discount}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Total + View link */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          flexShrink: 0,
          gap: "2px",
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 16,
            fontWeight: 700,
            color: "#09090b",
            letterSpacing: "-0.01em",
            lineHeight: 1.1,
          }}
        >
          {total}
        </Typography>
        {adminUrl && (
          <Box
            component="a"
            href={adminUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              fontSize: 12,
              fontWeight: 600,
              color: "#1d4ed8",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Orders view ──

function OrdersView({ orders }) {
  const [expanded, setExpanded] = useState(false);

  if (!orders || orders.length === 0) {
    return (
      <EmptyState
        icon="🧾"
        title="No orders"
        message="This customer has no orders yet."
      />
    );
  }
  const MAX = 4;
  const displayOrders = expanded ? orders : orders.slice(0, MAX);
  const hasMore = orders.length > MAX;
  const hiddenCount = orders.length - MAX;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: "14px",
          py: "10px",
          borderBottom: "1px solid #f4f4f5",
          bgcolor: "#fafafa",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ShoppingCartOutlinedIcon sx={{ fontSize: 17, color: "#0f172a" }} />
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 14,
              fontWeight: 700,
              color: "#09090b",
              letterSpacing: "-0.01em",
            }}
          >
            Order History
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: "#71717a" }}>
          {orders.length} order{orders.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      {/* Rows */}
      <Box sx={{ px: "14px" }}>
        {displayOrders.map((order, i) => (
          <OrderRow
            key={order.id || i}
            order={order}
            isLast={i === displayOrders.length - 1 && !hasMore}
          />
        ))}
      </Box>

      {hasMore && (
        <Box
          sx={{
            borderTop: "1px solid #f4f4f5",
            px: "14px",
            py: "10px",
            textAlign: "center",
            bgcolor: "#fafafa",
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded((v) => !v)}
            sx={{
              border: "none",
              background: "none",
              fontSize: 13,
              fontWeight: 600,
              color: "#1d4ed8",
              cursor: "pointer",
              fontFamily: "inherit",
              p: 0,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {expanded
              ? "Show less"
              : `Show ${hiddenCount} more order${hiddenCount === 1 ? "" : "s"}`}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Carts view ──

function CartsView({ carts }) {
  if (!carts || carts.length === 0) {
    return (
      <EmptyState
        icon="🛒"
        title="No abandoned carts"
        message="Nothing left in cart by this customer."
      />
    );
  }
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "12px",
        p: "12px 14px",
      }}
    >
      <SectionTitle>Abandoned Carts</SectionTitle>
      {carts.map((cart, i) => (
        <Box
          key={cart.id || i}
          sx={{
            p: "10px 0",
            borderBottom: "1px solid #f4f4f5",
            "&:last-child": { borderBottom: "none" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ fontSize: 14, fontWeight: 600 }}>
              {cart.name || cart.token || `Cart ${i + 1}`}
            </Box>
            <Box sx={{ fontSize: 14, fontWeight: 600 }}>
              {formatCurrency(cart.total_price)}
            </Box>
          </Box>
          <Box sx={{ fontSize: 13, color: "#71717a", mt: "2px" }}>
            {formatDate(cart.created_at || cart.updated_at)}
            {cart.line_items_count ? ` · ${cart.line_items_count} item(s)` : ""}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── Entity component ──────────────────────────────────────────────────────

export default function Contact({ data, recordData, storeDomain, loading }) {
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const customer = data || null;
  const orders = customer?.orders || [];
  const carts = customer?.abandoned_carts || [];

  return (
    // No storeDomain to Shell — it lives inside the CustomerCard now. `bare`
    // skips Shell's own card so we don't get nested borders. Tabs still
    // render in Shell's top bar.
    <Shell
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      loading={loading}
      bare
    >
      {activeTab === "segments" && (
        <SegmentsView
          customer={customer}
          recordData={recordData}
          storeDomain={storeDomain}
        />
      )}
      {activeTab === "orders" && <OrdersView orders={orders} />}
      {activeTab === "carts" && <CartsView carts={carts} />}
    </Shell>
  );
}
